// SIM-1870: shared top-right account menu. Consolidates identity + role-gated
// admin actions into a single avatar dropdown so the Admin and Super Admin
// Console links are reachable on EVERY authenticated page (home, dashboard
// viewer, and single- + multi-page SimplySpark dashboards), not just the spark
// rail footer. Sections are visually separated and role-gated via useUserRole,
// preserving the exact authorization the header/rail used before.
import HelpForm from "@/components/HelpForm";
import { useAuth, type PortalUser } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import { userInitials } from "@/lib/initials";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@simplicity/ui";
import { HelpCircle, LogOut, Settings, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

/** Green-accent circle with the user's initials. */
function UserAvatar({ email, name }: { email: string; name?: string | null }) {
  return (
    <span
      className="flex h-9 w-9 items-center justify-center rounded-full bg-success text-sm font-semibold text-success-foreground"
      aria-hidden="true"
      data-testid="user-avatar"
    >
      {userInitials(email, name)}
    </span>
  );
}

/** Identity block: full name (when present) above the email, else email only. */
function AccountIdentity({ user }: { user: PortalUser }) {
  if (user.name?.trim()) {
    return (
      <DropdownMenuLabel className="font-normal" data-testid="user-menu-identity">
        <div className="max-w-[14rem] truncate font-medium text-foreground">{user.name}</div>
        <div className="max-w-[14rem] truncate text-xs text-muted-foreground">({user.email})</div>
      </DropdownMenuLabel>
    );
  }
  return (
    <DropdownMenuLabel
      className="max-w-[14rem] truncate font-normal text-muted-foreground"
      data-testid="user-menu-identity"
    >
      {user.email}
    </DropdownMenuLabel>
  );
}

/**
 * Admin section: Organization Settings (visible to admins) + the Super Admin
 * Console (visible to super admins). Rendered as its own separated section with
 * a heading so the elevated actions are visually distinct from the general menu.
 * Renders nothing when the user is neither an admin nor a super admin.
 */
function AdminSection({
  isAdmin,
  isSuperAdmin,
  settingsPath,
  onNavigate,
}: {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  settingsPath: string;
  onNavigate: (path: string) => void;
}) {
  if (!isAdmin && !isSuperAdmin) return null;
  return (
    <>
      <DropdownMenuSeparator />
      {isAdmin && (
        <>
          <DropdownMenuLabel
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            data-testid="account-menu-admin-heading"
          >
            Admin
          </DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() => onNavigate(settingsPath)}
            data-testid="user-menu-settings"
          >
            <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
            Settings
          </DropdownMenuItem>
        </>
      )}
      {isSuperAdmin && (
        <>
          <DropdownMenuLabel
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            data-testid="account-menu-superadmin-heading"
          >
            Super Admin
          </DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() => onNavigate("/super-admin")}
            data-testid="user-menu-superadmin"
          >
            <ShieldAlert className="mr-2 h-4 w-4" aria-hidden="true" />
            Super Admin Console
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}

/** General section: Help request + Sign out. Always present. */
function GeneralSection({
  onOpenHelp,
  onSignOut,
}: {
  onOpenHelp: () => void;
  onSignOut: () => void;
}) {
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onOpenHelp} data-testid="user-menu-help">
        <HelpCircle className="mr-2 h-4 w-4" aria-hidden="true" />
        Help request
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={onSignOut} data-testid="user-menu-logout">
        <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
        Sign out
      </DropdownMenuItem>
    </>
  );
}

/**
 * SIM-1870: the single shared account menu. Self-contained — reads identity
 * (useAuth), role (useUserRole), and tenant context (useTenant) directly, so it
 * can mount on any authenticated surface without prop plumbing. Renders nothing
 * when there's no signed-in user.
 */
export default function AccountMenu() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isAdmin, isSuperAdmin } = useUserRole();
  const { currentTenantSlug, tenants } = useTenant();
  const [helpOpen, setHelpOpen] = useState(false);

  if (!user) return null;

  // On global routes (e.g. /super-admin) there's no tenant context; fall back
  // to the first tenant's slug for the settings link.
  const fallbackSlug = currentTenantSlug ?? tenants[0]?.slug;
  const settingsPath = fallbackSlug ? `/${fallbackSlug}/settings` : "/";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full hover:bg-white/10"
            aria-label="Account menu"
            data-testid="user-menu-trigger"
          >
            <UserAvatar email={user.email} name={user.name} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          <AccountIdentity user={user} />
          <AdminSection
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            settingsPath={settingsPath}
            onNavigate={(path) => void navigate(path)}
          />
          <GeneralSection onOpenHelp={() => setHelpOpen(true)} onSignOut={() => void signOut()} />
        </DropdownMenuContent>
      </DropdownMenu>
      <HelpForm open={helpOpen} onOpenChange={setHelpOpen} hideTrigger />
    </>
  );
}
